import Cursor from "./cursor";
import { IOperationType } from "./internals";
import { liftCursor, newSelection, newText, normalizeFormat } from "./utils";
class AbstractPerformChangeContext {
    constructor(model) {
        this.model = model;
    }
    empty() {
        this.select().delete();
        return this;
    }
    load(content) {
        this.model.setContent(content);
        return this;
    }
    updateElementProps(path, newProps) {
        this.model.updateElementProps(path, newProps);
        return this;
    }
    select(offset = null, endOffset = null, backward = false) {
        if (offset === null && endOffset === null) {
            offset = 0;
            endOffset = this.model.content.length;
        }
        else {
            if (offset === null) {
                offset = 0;
            }
            if (endOffset === null) {
                endOffset = offset;
            }
        }
        this.selection = newSelection(backward ? endOffset : offset, backward ? offset : endOffset);
        return this;
    }
    collapse(type = "focus") {
        if (this.selection !== null) {
            let offset = 0;
            switch (type) {
                case "start": {
                    offset = this.selection.offset;
                    break;
                }
                case "end": {
                    offset = this.selection.endOffset;
                    break;
                }
                case "anchor": {
                    offset = this.selection.anchorOffset;
                    break;
                }
                case "focus": {
                    offset = this.selection.focusOffset;
                    break;
                }
            }
            this.selection = newSelection(offset, offset);
        }
        return this;
    }
    enlarge() {
        if (this.selection === null)
            return this;
        let startOffset = this.selection.offset;
        let endOffset = this.selection.endOffset;
        startOffset = liftCursor(this.model.content, startOffset, "backward");
        endOffset = liftCursor(this.model.content, endOffset, "forward");
        const isBackward = this.selection.isBackward;
        this.selection = newSelection(isBackward ? endOffset : startOffset, isBackward ? startOffset : endOffset);
        return this;
    }
    move(steps, units = "character") {
        if (this.selection !== null) {
            if (units === "character") {
                let focusOffset = this.selection.focusOffset;
                focusOffset = Math.min(Math.max(0, focusOffset + steps), this.model.content.length);
                this.selection = newSelection(this.selection.anchorOffset, focusOffset);
            }
        }
        return this;
    }
    setFormat(format, apply = false) {
        if (apply && this.selection !== null && !this.selection.isCollapsed) {
            this.model.applyOperation({
                type: IOperationType.FORMAT,
                offset: this.selection.offset,
                length: this.selection.length,
                format,
            });
        }
        this.format = normalizeFormat(format, this.format);
        return this;
    }
    applyFormat(format) {
        return this.setFormat(format, true);
    }
    delete() {
        var _a, _b, _c, _d;
        this.model.applyOperation({
            type: IOperationType.DELETE,
            offset: (_b = (_a = this.selection) === null || _a === void 0 ? void 0 : _a.offset) !== null && _b !== void 0 ? _b : 0,
            length: (_d = (_c = this.selection) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0,
        });
        return this;
    }
    insert(content) {
        var _a, _b;
        if (typeof content === "string") {
            const text = newText(content, this.format);
            content = {
                items: [text],
                length: text.length,
            };
        }
        this.model.applyOperation({
            type: IOperationType.INSERT,
            offset: (_b = (_a = this.selection) === null || _a === void 0 ? void 0 : _a.focusOffset) !== null && _b !== void 0 ? _b : 0,
            content,
        });
        return this;
    }
    replace(content) {
        if (typeof content === "string") {
            const text = newText(content, this.format);
            content = {
                items: [text],
                length: text.length,
            };
        }
        if (this.selection === null) {
            this.model.setContent(content, null);
        }
        else {
            this.model.applyOperation({
                type: IOperationType.REPLACE,
                offset: this.selection.offset,
                length: this.selection.length,
                content,
            });
        }
        return this;
    }
    wrap(tag, props) {
        if (this.selection === null || this.selection.isCollapsed)
            return this;
        this.enlarge();
        this.model.applyOperation({
            type: IOperationType.WRAP,
            offset: this.selection.offset,
            length: this.selection.length,
            tag,
            props,
        });
        return this;
    }
    unwrap(tag, path) {
        this.model.applyOperation({
            type: IOperationType.UNWRAP,
            path,
            tag,
        });
        return this;
    }
}
export default class PerformChangeContext extends AbstractPerformChangeContext {
    constructor() {
        super(...arguments);
        this.shadows = new Set();
    }
    get selection() {
        return this.model.selection;
    }
    set selection(newSelection) {
        this.model.setSelection(newSelection);
    }
    get format() {
        return this.model.format;
    }
    set format(newFormat) {
        this.model.setFormat(newFormat);
    }
    shadow() {
        return new ShadowPerformChangeContext(this);
    }
    release() {
        for (const shadow of this.shadows) {
            shadow.release();
        }
    }
}
class ShadowPerformChangeContext extends AbstractPerformChangeContext {
    constructor(context) {
        super(context.model);
        this.context = context;
        this.startCursor = null;
        this.endCursor = null;
        this.context.shadows.add(this);
        this.format = this.context.format;
        this.selection = this.context.selection;
    }
    get selection() {
        if (this.startCursor === null || this.endCursor === null)
            return null;
        return newSelection(this.startCursor.offset, this.endCursor.offset);
    }
    set selection(selection) {
        this.releaseCursors();
        if (selection !== null) {
            this.startCursor = new Cursor(this.context.model, selection.offset, "left");
            this.endCursor = new Cursor(this.context.model, selection.endOffset, "right");
        }
    }
    shadow() {
        return new ShadowPerformChangeContext(this.context);
    }
    releaseCursors() {
        if (this.startCursor !== null) {
            this.startCursor.release();
            this.startCursor = null;
        }
        if (this.endCursor !== null) {
            this.endCursor.release();
            this.endCursor = null;
        }
    }
    release() {
        this.releaseCursors();
    }
}
